import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Icon,
  Separator,
  Text,
  useIsMobile,
  type BulkAction,
} from "@salestrak/ui";
import { PageHeader } from "#app/components/page-header";
import { MembersTable, buildMembersFilterDefs } from "#app/components/members/members-table";
import { InviteMembersDialog } from "#app/components/members/invite-members-dialog";
import { useListQueryState } from "#app/lib/hooks/use-list-query-state";
import { MEMBER_LIST } from "#app/lib/filters/list-configs";
import type { MemberRow, RoleRow, TeamOption } from "#app/lib/data";
import { useMemberMutations } from "#app/lib/hooks/use-member-mutations";
import { usePermission } from "#app/lib/hooks/use-permission";
import { settleAll } from "#app/lib/utils/settle-all";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useTableFilters } from "#app/lib/hooks/use-table-filters";
import { withPermission } from "#app/components/permissions/require-permission";

function SettingsMembersPage() {
  const isMobile = useIsMobile();
  const { push } = useAppNavigate();

  // Sibling data extracted from the ungrouped query.
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const handleSiblingData = useCallback((r: RoleRow[], t: TeamOption[]) => {
    setRoles(r);
    setTeams(t);
  }, []);

  const filterDefs = useMemo(() => buildMembersFilterDefs(teams, roles, "settings"), [teams, roles]);
  const memberFilters = useTableFilters(filterDefs);

  const { searchValue, setSearchValue, sorting, setSorting, searchWhere, orderBy, where } =
    useListQueryState(MEMBER_LIST, { filterWhere: memberFilters.where });

  const { updateMember, updateMemberRoles, updateMemberTeams, resendInvite, cancelInvite } = useMemberMutations();
  const canUpdateBase = usePermission("manage.users");
  const canUpdateRoles = usePermission("manage.users");
  const canInvite = usePermission("manage.users");

  const [inviteOpen, setInviteOpen] = useState(false);

  // --- Bulk actions config (settings variant only) ---
  const bulkActions = useMemo<BulkAction<MemberRow>[]>(() => {
    const actions: BulkAction<MemberRow>[] = [];

    if (canUpdateRoles && roles.length > 0) {
      actions.push({
        id: "change-role",
        type: "option-picker",
        label: "Change role",
        icon: "shield",
        options: roles.map((r) => ({ id: r.roleId, label: r.roleName })),
        onAction: async (selected, roleId) => {
          await settleAll(selected, (m) => {
            const current = m.roles.map((r) => r.roleId);
            return updateMemberRoles(m.userId, {
              add: [roleId],
              remove: current.filter((id) => id !== roleId),
            });
          });
        },
      });
    }

    if (canUpdateBase) {
      actions.push({
        id: "suspend",
        type: "confirm",
        label: "Suspend",
        icon: "x-circle",
        destructive: true,
        hidden: (rows) => !rows.some((m) => m.memberStatus === "active"),
        confirmTitle: (rows) =>
          `Suspend ${rows.length} member${rows.length !== 1 ? "s" : ""}?`,
        confirmDescription: () =>
          "Suspended members will lose access to this workspace.",
        confirmLabel: "Suspend",
        onAction: async (selected) => {
          await settleAll(
            selected.filter((m) => m.memberStatus === "active"),
            (m) => updateMember(m.userId, { memberStatus: "suspended" }),
          );
        },
      });

      actions.push({
        id: "reactivate",
        type: "direct",
        label: "Reactivate",
        icon: "play-circle",
        hidden: (rows) => !rows.some((m) => m.memberStatus === "suspended"),
        onAction: async (selected) => {
          await settleAll(
            selected.filter((m) => m.memberStatus === "suspended"),
            (m) => updateMember(m.userId, { memberStatus: "active" }),
          );
        },
      });
    }

    if (canInvite) {
      actions.push({
        id: "resend-invite",
        type: "direct",
        label: "Resend invite",
        icon: "send",
        hidden: (rows) => !rows.some((m) => m.memberStatus === "invited"),
        onAction: async (selected) => {
          await settleAll(
            selected.filter((m) => m.memberStatus === "invited"),
            (m) => resendInvite(m.userId),
          );
        },
      });

      actions.push({
        id: "cancel-invite",
        type: "confirm",
        label: "Cancel invite",
        icon: "x-circle",
        destructive: true,
        hidden: (rows) => !rows.some((m) => m.memberStatus === "invited"),
        confirmTitle: (rows) => {
          const count = rows.filter((m) => m.memberStatus === "invited").length;
          return `Cancel ${count} invite${count !== 1 ? "s" : ""}?`;
        },
        confirmDescription: () =>
          "Invitees will be removed from the workspace and their invitation links will stop working.",
        confirmLabel: "Cancel invite",
        onAction: async (selected) => {
          await settleAll(
            selected.filter((m) => m.memberStatus === "invited"),
            (m) => cancelInvite(m.userId),
          );
        },
      });
    }

    return actions;
  }, [
    canUpdateBase,
    canUpdateRoles,
    canInvite,
    roles,
    updateMember,
    updateMemberRoles,
    resendInvite,
    cancelInvite,
  ]);

  const handleTeamsChange = useCallback(
    async (userId: string, currentTeamIds: string[], newTeamIds: string[]) => {
      await updateMemberTeams(userId, currentTeamIds, newTeamIds);
    },
    [updateMemberTeams]
  );

  const handleRoleChange = useCallback(
    async (userId: string, currentRoleIds: string[], roleId: string) => {
      const isSelected = currentRoleIds.includes(roleId);
      const add = isSelected ? [] : [roleId];
      const remove = isSelected ? [roleId] : currentRoleIds.filter((id) => id !== roleId);
      await updateMemberRoles(userId, { add, remove });
    },
    [updateMemberRoles]
  );

  const handleNavigateToProfile = useCallback(
    (userId: string) => {
      push(`/settings/members/${userId}`);
    },
    [push]
  );

  return (
    <Box direction="column" fill="flex">
      <PageHeader
        title="Members"
        actions={
          !isMobile && canInvite ? (
            <Button size="xs" onClick={() => setInviteOpen(true)}>
              <Icon name="mail" />
              <Text>Invite members</Text>
            </Button>
          ) : undefined
        }
      />
      {!isMobile && <Separator />}
      <MembersTable
        variant="settings"
        filterState={memberFilters}
        where={where}
        orderBy={orderBy}
        sorting={sorting}
        onSortingChange={setSorting}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        availableTeams={teams}
        availableRoles={roles}
        onNavigateToProfile={handleNavigateToProfile}
        onTeamsChange={handleTeamsChange}
        onRoleChange={handleRoleChange}
        searchWhere={searchWhere}
        onSiblingData={handleSiblingData}
        bulkActions={bulkActions}
        actionButton={
          isMobile && canInvite ? (
            <Button
              aria-label="Invite members"
              size="icon-xs"
              onClick={() => setInviteOpen(true)}
            >
              <Icon name="plus" />
            </Button>
          ) : undefined
        }
      />
      {canInvite && (
        <InviteMembersDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
        />
      )}
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/members/")({
  component: withPermission("manage.users", SettingsMembersPage),
});
